import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DeviceSettingArea,
  DeviceSettingLeaf,
  DeviceSettingObject,
  DeviceSettings,
  SetDeviceSettingsResult,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Button, ErrorNotice, Input, Pill, Spinner } from '../../components/ui';
import { CollapsibleGroup, useDetailsItem, useUnsavedDraft } from '../../components/ui/CollapsibleGroup';
import { Segmented } from './SecurityControls';
import { formatDateTime } from '../../lib/format';

type Notify = (message: string, tone?: 'info' | 'danger') => void;

const enc = encodeURIComponent;

/**
 * Every setting the router reports, from its own parameter tree, editable
 * where the router allows it (operator, 2026-10-05: "make the config tab of
 * onu details page dynamic, show us and allow us to configure whatever the onu
 * allows"). `GET /devices/:id/settings` sorts it into areas and labels it; the
 * few leaves the platform will not write from here come back with the reason
 * and are shown, not hidden. Each object is its own staged form — edits
 * collect, one Save writes that object's changes in one request, and the API
 * checks every leaf against the router again before anything is sent.
 *
 * It replaces the read-only "All TR-069 parameters" list: "Show read-only
 * too" is that list, labelled.
 */
export function AllSettings({ deviceId, onNotice }: { deviceId: string; onNotice: Notify }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const queryKey = ['device', deviceId, 'settings', showAll ? 'all' : 'writable'];
  const q = useQuery({
    queryKey,
    queryFn: () =>
      api.get<DeviceSettings>(`/devices/${enc(deviceId)}/settings${showAll ? '?all=true' : ''}`),
  });

  const reread = useMutation({
    mutationFn: () =>
      api.post<{ delivered: boolean }>(`/devices/${enc(deviceId)}/actions`, { action: 'refresh' }),
    onSuccess: async (r) => {
      onNotice(
        r.delivered
          ? 'Read every setting from the router again.'
          : 'The router did not answer; it is asked again at its next inform.',
      );
      await queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'settings'] });
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not ask the router.', 'danger'),
  });

  const term = search.trim().toLowerCase();
  const areas = useMemo(() => {
    const all = q.data?.areas ?? [];
    if (!term) return all;
    return all
      .map((area) => ({
        ...area,
        objects: area.objects
          .map((obj) => {
            if (obj.title.toLowerCase().includes(term)) return obj;
            const leaves = obj.leaves.filter((l) =>
              `${l.label} ${l.path} ${l.secret || l.value === null ? '' : String(l.value)}`
                .toLowerCase()
                .includes(term),
            );
            return leaves.length > 0 ? { ...obj, leaves } : null;
          })
          .filter((o): o is DeviceSettingObject => o !== null),
      }))
      .filter((area) => area.objects.length > 0);
  }, [q.data, term]);

  if (q.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }
  if (q.error) return <div className="p-4"><ErrorNotice error={q.error} /></div>;
  const data = q.data;
  if (!data || data.total === 0) {
    return (
      <p className="p-5 text-sm text-muted">
        The ACS holds no settings for this router yet. Summon it, or read it again, and they appear here.
      </p>
    );
  }
  const held = data.areas.reduce(
    (n, a) => n + a.objects.reduce((m, o) => m + o.leaves.filter((l) => l.writable && l.guard).length, 0),
    0,
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-prose text-sm text-muted">
          <span className="text-ink">{data.changeable}</span> settings on this router can be changed here
          {held > 0 && (
            <>
              ; <span className="text-ink">{held}</span> more are held back, shown with the reason
            </>
          )}
          . Read off the router itself{data.lastInform ? `, last in touch ${formatDateTime(data.lastInform, { seconds: true })}` : ''}.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-muted">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Show read-only too
          </label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a setting…"
            className="w-64"
            aria-label="Find a setting"
          />
          <Button size="sm" loading={reread.isPending} onClick={() => reread.mutate()}>
            Read the router again
          </Button>
        </div>
      </div>

      {areas.length === 0 ? (
        <p className="text-sm text-muted">Nothing matches “{search.trim()}”.</p>
      ) : (
        /* One area open at a time, as everywhere on this page — except while
           finding, when every area with a match opens so it can be read. */
        <CollapsibleGroup>
          {areas.map((area) => (
            <AreaPanel
              key={area.key}
              area={area}
              deviceId={deviceId}
              forcedOpen={Boolean(term)}
              onNotice={onNotice}
            />
          ))}
        </CollapsibleGroup>
      )}
    </div>
  );
}

function AreaPanel({
  area,
  deviceId,
  forcedOpen,
  onNotice,
}: {
  area: DeviceSettingArea;
  deviceId: string;
  forcedOpen: boolean;
  onNotice: Notify;
}) {
  const { open, onToggle } = useDetailsItem(`settings-${area.key}`);
  return (
    <details
      className="card overflow-hidden"
      open={forcedOpen || open}
      onToggle={forcedOpen ? undefined : onToggle}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-3 hover:bg-surface-2">
        <span className="text-sm font-medium text-ink">{area.label}</span>
        <span className="text-xs text-faint">
          {area.changeable > 0 ? `${area.changeable} to change` : 'nothing to change'}
          {' · '}
          {area.total} reported
        </span>
      </summary>
      <div className="divide-y divide-line border-t border-line">
        {area.objects.map((obj) => (
          <ObjectEditor key={obj.path} obj={obj} deviceId={deviceId} onNotice={onNotice} />
        ))}
      </div>
    </details>
  );
}

type Draft = Record<string, string | boolean>;

/** One TR-069 object — a WiFi network, a WAN connection, the clock — as a staged form. */
function ObjectEditor({
  obj,
  deviceId,
  onNotice,
}: {
  obj: DeviceSettingObject;
  deviceId: string;
  onNotice: Notify;
}) {
  const { can } = useSession();
  const canWrite = can('device.write');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>({});

  const editable = (l: DeviceSettingLeaf) => canWrite && l.writable && !l.guard;
  const isDirty = (l: DeviceSettingLeaf) => {
    if (!(l.path in draft)) return false;
    const v = draft[l.path];
    if (l.secret) return typeof v === 'string' && v.length > 0;
    return String(v) !== String(l.value ?? '');
  };
  const dirty = obj.leaves.filter(isDirty);
  useUnsavedDraft(dirty.length > 0);

  const save = useMutation({
    mutationFn: () =>
      api.post<SetDeviceSettingsResult>(`/devices/${enc(deviceId)}/settings`, {
        values: dirty.map((l) => ({ path: l.path, value: draft[l.path]! })),
      }),
    onSuccess: async (r) => {
      onNotice(`${obj.title}: ${r.message}`);
      setDraft({});
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'settings'] }),
        queryClient.invalidateQueries({ queryKey: ['device', deviceId] }),
      ]);
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not save.', 'danger'),
  });

  // A reason several fields share is said once, under the title; those fields
  // keep only the pill. A reason one field has stays beside it.
  const reasonCounts = new Map<string, number>();
  for (const l of obj.leaves) {
    if (l.guard) reasonCounts.set(l.guard.reason, (reasonCounts.get(l.guard.reason) ?? 0) + 1);
  }
  const shared = [...reasonCounts].filter(([, n]) => n > 1).map(([reason]) => reason);

  return (
    <div className="px-5 py-3">
      <div className="mb-1">
        <div className="text-sm font-medium text-ink">{obj.title}</div>
        {obj.note && <p className="mt-0.5 text-xs text-muted">{obj.note}</p>}
        {shared.map((reason) => (
          <p key={reason} className="mt-1 text-xs text-warn">
            Held back here: {reason}
          </p>
        ))}
      </div>
      <div className="divide-y divide-line/60">
        {obj.leaves.map((l) => (
          <LeafRow
            key={l.path}
            leaf={l}
            showReason={Boolean(l.guard) && !shared.includes(l.guard!.reason)}
            editable={editable(l)}
            value={l.path in draft ? draft[l.path]! : null}
            onChange={(v) => setDraft((d) => ({ ...d, [l.path]: v }))}
          />
        ))}
      </div>
      {dirty.length > 0 && (
        <div className="mt-2 flex items-center justify-end gap-2">
          <span className="mr-auto text-xs text-muted">
            {dirty.length === 1 ? '1 change' : `${dirty.length} changes`} not yet sent
          </span>
          <Button size="sm" onClick={() => setDraft({})} disabled={save.isPending}>
            Reset
          </Button>
          <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

const GUARD_PILL: Record<NonNullable<DeviceSettingLeaf['guard']>['kind'], string> = {
  lifeline: 'keeps the ONU reachable',
  editor: 'changed elsewhere',
  action: 'an action',
};

function shownValue(l: DeviceSettingLeaf): string {
  if (l.secret) return l.writable ? 'hidden' : '••••••••';
  if (l.value === null || l.value === '') return 'empty';
  if (typeof l.value === 'boolean') return l.value ? 'On' : 'Off';
  if (l.type === 'xsd:dateTime') return shownDateTime(String(l.value));
  return String(l.value);
}

/**
 * A router's xsd:dateTime on the platform's clock (EAT) — only when it states
 * its offset (`Z` or `+03:00`); a bare `2026-10-05T17:17:09` is the router's
 * own local time in a zone it does not name, so it is shown as sent. 196 of
 * the fleet's routers run on GMT (2026-10-05), so their stamps read three
 * hours early unless converted. Before 2000 the router has never set its
 * clock (TR-069's own "unknown time" is 0001-01-01), which is said.
 */
function shownDateTime(raw: string): string {
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) return raw;
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return raw;
  const when = formatDateTime(t, { seconds: true });
  return new Date(t).getUTCFullYear() < 2000 ? `${when} — clock not set` : `${when} EAT`;
}

function isNumeric(type: string): boolean {
  return /^xsd:(unsigned)?(int|long|short|byte)$/i.test(type);
}

function LeafRow({
  leaf: l,
  showReason,
  editable,
  value,
  onChange,
}: {
  leaf: DeviceSettingLeaf;
  /** Say why it is held, unless the object already said it once for several fields. */
  showReason: boolean;
  editable: boolean;
  /** The staged edit, when there is one. */
  value: string | boolean | null;
  onChange: (v: string | boolean) => void;
}) {
  const isBool = l.type === 'xsd:boolean' && (typeof l.value === 'boolean' || l.value === null);
  const current = value ?? l.value;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-ink" title={l.readAt ? `Read ${formatDateTime(l.readAt, { seconds: true })}` : undefined}>
          {l.label}
        </div>
        <div className="truncate font-mono text-[11px] text-faint" title={l.path}>
          {l.name}
        </div>
        {showReason && l.guard && <p className="mt-0.5 text-xs text-muted">{l.guard.reason}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {l.guard && <Pill state tone={l.guard.kind === 'lifeline' ? 'warn' : 'neutral'}>{GUARD_PILL[l.guard.kind]}</Pill>}
        {!editable ? (
          <span className="max-w-[16rem] truncate font-mono text-xs text-muted" title={l.secret ? undefined : String(l.value ?? '')}>
            {shownValue(l)}
          </span>
        ) : isBool ? (
          <Segmented value={current === true} onChange={(v) => onChange(v)} />
        ) : (
          <Input
            type={l.secret ? 'password' : isNumeric(l.type) ? 'number' : 'text'}
            inputMode={isNumeric(l.type) ? 'numeric' : undefined}
            min={/unsigned/i.test(l.type) ? 0 : undefined}
            className="w-56"
            autoComplete={l.secret ? 'new-password' : 'off'}
            placeholder={l.secret ? 'type a new one to change it' : ''}
            aria-label={l.label}
            value={typeof current === 'string' || typeof current === 'number' ? String(current) : ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
    </div>
  );
}
